import { errorHandler } from './errorHandler';

// Verrous d'édition partagés entre les onglets du navigateur (Web Locks API) : une saisie n'est modifiable que dans
// l'onglet qui tient son verrou, que le navigateur libère à la fermeture de cet onglet.
// Sans verrous (API absente ou en erreur), les saisies restent modifiables, sans protection entre onglets.
const tag = '80.quickConnect.Core/helpers/editLock.ts';

/** Verrou d'une déclaration (nouvelle saisie, saisie interrompue ou brouillon) */
export const getDeclarationEditLockName = (declarationId: string): string => `qc-edit-declaration-${declarationId}`;

/** Verrou d'un élément de la boîte de réception */
export const getInboxEditLockName = (inboxId: string): string => `qc-edit-inbox-${inboxId}`;

/** Verrou partagé par les onglets qui saisissent ce formulaire : indique seulement qu'une saisie y est en cours */
export const getFormEditLockName = (formId: string): string => `qc-edit-form-${formId}`;

/** Noms des verrous tenus par l'ensemble des onglets */
export const getHeldEditLockNamesAsync = async (): Promise<Set<string>> => {
  if (!navigator.locks) return new Set<string>();

  try {
    const { held = [] } = await navigator.locks.query();

    return new Set(held.map(({ name }) => name ?? ''));
  } catch (error: unknown) {
    errorHandler(tag, error, 'getHeldEditLockNamesAsync');
    return new Set<string>();
  }
};

/**
 * Exécute l'action en tenant le verrou, pour qu'aucun onglet ne prenne la saisie entre-temps.
 * Retourne false, sans exécuter l'action, si le verrou est déjà tenu, y compris par l'onglet courant.
 * Les erreurs de l'action sont propagées.
 */
export const runWithEditLockAsync = async (name: string, action: () => Promise<unknown>): Promise<boolean> => {
  if (navigator.locks) {
    let isGranted = false;

    try {
      return await navigator.locks.request(name, { ifAvailable: true }, async (lock: Lock | null) => {
        if (!lock) return false;

        isGranted = true;
        await action();
        return true;
      });
    } catch (error: unknown) {
      if (isGranted) throw error;

      errorHandler(tag, error, 'runWithEditLockAsync');
    }
  }

  await action();
  return true;
};

/**
 * Prend le verrou jusqu'à l'appel de la fonction retournée, ou jusqu'à la fermeture de l'onglet.
 * Retourne undefined si le verrou est déjà tenu (un verrou partagé ne l'est jamais : plusieurs onglets le tiennent).
 */
export const acquireEditLockAsync = (name: string, mode: LockMode = 'exclusive'): Promise<(() => void) | undefined> => {
  const withoutLock = () => undefined;

  if (!navigator.locks) return Promise.resolve(withoutLock);

  return new Promise((resolve) => {
    navigator.locks
      .request(name, { mode, ifAvailable: true }, (lock: Lock | null) => {
        if (!lock) {
          resolve(undefined);
          return undefined;
        }

        // Le verrou est tenu tant que cette promesse n'est pas résolue
        return new Promise<void>((release) => resolve(() => release()));
      })
      .catch((error: unknown) => {
        errorHandler(tag, error, 'acquireEditLockAsync');
        resolve(withoutLock);
      });
  });
};
